import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Plus, Trash2, FileText, Upload, Save, Loader2, CheckCircle2, File } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import EmptyState from '@/components/EmptyState';
import { useDemo } from '@/contexts/DemoContext';

/**
 * DocumentsPage - Important documents for Emergency Binder
 */

const DOCUMENT_TYPES = [
  { value: 'drivers_license', label: "Driver's License" },
  { value: 'passport', label: 'Passport' },
  { value: 'ssn', label: 'Social Security Card' },
  { value: 'birth_cert', label: 'Birth Certificate' },
  { value: 'marriage_cert', label: 'Marriage Certificate' },
  { value: 'insurance', label: 'Insurance Documents' },
  { value: 'medical', label: 'Medical Records' },
  { value: 'other', label: 'Other' },
];

const DocumentsPage = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const { isDemoMode, getProfiles, getDocuments } = useDemo();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    fetchProfile();
  }, [profileId, isDemoMode]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      
      if (isDemoMode) {
        const profiles = getProfiles() || [];
        const demoProfile = profiles.find(p => p.id === profileId) || profiles[0];
        const allDocs = getDocuments() || [];
        // Filter documents for this profile or generic ones
        const profileDocs = allDocs.filter(d => 
          d.profile_id === profileId || !d.profile_id
        ).slice(0, 5); // Just show a few
        setProfile(demoProfile);
        setDocuments(profileDocs);
        setLoading(false);
        return;
      }
      
      const response = await API.get(`/emergency-binder/profiles/${profileId}`);
      setProfile(response.data);
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      if (!isDemoMode) {
        toast.error('Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (isDemoMode) {
        toast.success('Documents saved (demo)');
        setSaving(false);
        return;
      }
      
      await API.put(`/emergency-binder/profiles/${profileId}`, {
        documents: documents
      });
      toast.success('Documents saved');
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save documents');
    } finally {
      setSaving(false);
    }
  };

  const addDocument = () => {
    setDocuments([...documents, {
      id: `temp_${Date.now()}`,
      name: '',
      document_type: '',
      file_url: '',
      notes: '',
      uploaded_at: new Date().toISOString()
    }]);
  };

  const updateDocument = (index, field, value) => {
    setDocuments(documents.map((d, i) => i === index ? { ...d, [field]: value } : d));
  };

  const removeDocument = (index) => {
    setDocuments(documents.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="documents-page">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/emergency-binder`)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-slate-800">Important Documents</h1>
              <p className="text-xs text-slate-500">{profile?.name}</p>
            </div>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-500" />
                Documents
              </CardTitle>
              <Button variant="outline" size="sm" onClick={addDocument} data-testid="add-document-btn">
                <Plus className="h-4 w-4 mr-1" />
                Add Document
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {documents.length === 0 ? (
              <EmptyState 
                type="documents"
                onAction={addDocument}
                actionLabel="Add First Document"
              />
            ) : (
              documents.map((doc, index) => (
                <div key={doc.id || index} className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 space-y-3">
                      {/* Document name - full width */}
                      <Input
                        placeholder="Document name"
                        value={doc.name || ''}
                        onChange={(e) => updateDocument(index, 'name', e.target.value)}
                      />
                      {/* Type - full width */}
                      <Select
                        value={doc.document_type || ''}
                        onValueChange={(value) => updateDocument(index, 'document_type', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Document type" />
                        </SelectTrigger>
                        <SelectContent>
                          {DOCUMENT_TYPES.map(type => (
                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {/* Notes - full width */}
                      <Input
                        placeholder="Notes (optional)"
                        value={doc.notes || ''}
                        onChange={(e) => updateDocument(index, 'notes', e.target.value)}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 flex-shrink-0"
                      onClick={() => removeDocument(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Button onClick={handleSave} disabled={saving} className="w-full h-12 bg-blue-600 hover:bg-blue-700">
          {saving ? (
            <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</>
          ) : (
            <><CheckCircle2 className="h-4 w-4 mr-2" />Save Documents</>
          )}
        </Button>
      </main>
    </div>
  );
};

export default DocumentsPage;
